import { defineStore } from "pinia";
import { ref, computed } from "vue";
import { useDebouncedSearchQuery } from "@utils/composables/filters/useDebouncedSearchQuery";
import { Globe, Copy, Trash } from "lucide-vue-next";
import useMyFetch from "./useMyFetch";
import handleError from "@utils/composables/handleError";
import { useAccountsStore } from "./accounts";
import { isEmpty } from "@utils/toolBox";
import { useSyncMultiCountryModal } from "@stores/mSyncMultiCountryModal";
import { captureEvent } from "@/lib/analytics";

export const useMCampaignsStore = defineStore("mCampaigns", () => {
  const accountsStore = useAccountsStore();
  const mCampaigns = ref({});
  const mCampaignsSearchQuery = ref("");
  const mCampaignsCount = ref(0);
  const currentPage = ref(1);
  const surveysStatusFilter = ref([]);
  const surveysAccountsFilter = ref([]);
  const mCampaignsStatusFilter = ref([]);
  const actionDropdownMCampaigns = ref([]);
  const { showModal } = useSyncMultiCountryModal();
  const visibleCampaign = ref(-1);

  const clearVisibleCampaign = () => (visibleCampaign.value = -1);

  // actions
  async function fetchMCampaigns(page = 1) {
    clearVisibleCampaign();

    try {
      const { response, data } = await useMyFetch(`/m_campaigns?page=${page}`).json();

      if (isEmpty(accountsStore.accounts)) await accountsStore.fetch();
      if (response.value.ok) {
        const { m_campaigns, m_campaigns_count } = data.value;
        mCampaigns.value = m_campaigns;
        mCampaignsCount.value = m_campaigns_count;
      }
      return mCampaigns.value;
    } catch (err) {
      handleError(err);
    }
  }

  const mCampaignsLoading = ref(false);

  async function search(page = 1) {
    clearVisibleCampaign();
    mCampaignsLoading.value = true;

    try {
      const params = {
        surveyStatus: surveysStatusFilter.value,
        account_id: surveysAccountsFilter.value,
        name: mCampaignsSearchQuery.value,
        status: mCampaignsStatusFilter.value,
        page: page,
      };
      const { response, data } = await useMyFetch(`/m_campaigns/search`).json().post(params, null);

      if (response.value.ok) {
        const { m_campaigns, m_campaigns_count } = data.value;
        mCampaigns.value = m_campaigns;
        mCampaignsCount.value = m_campaigns_count;
        currentPage.value = page;
      }

      return mCampaigns.value;
    } catch (err) {
      handleError(err);
    } finally {
      mCampaignsLoading.value = false;
    }
  }

  const changePage = async (newPage) => {
    currentPage.value = newPage;
    await search(newPage);
  };

  const resetAllFilters = async () => {
    surveysStatusFilter.value = [];
    surveysAccountsFilter.value = [];
    resetSearchQuery();
    mCampaignsStatusFilter.value = [];
    currentPage.value = 1;
  };
  const clearFilters = async () => {
    surveysStatusFilter.value = [];
    mCampaignsStatusFilter.value = [];
    surveysAccountsFilter.value = [];
    await search();
  };

  const syncMCampaignStatus = (mCampaignId, status) => {
    const mCampaign = mCampaigns.value.find((mCampaign) => mCampaign.id === mCampaignId);
    if (mCampaign) mCampaign.status = status;
  };

  async function duplicateMCampaign(token) {
    clearVisibleCampaign();

    try {
      const { data: copyCampaign, response } = await useMyFetch(`/m_campaigns/${token}/duplicate`).json().post();

      if (response.value.ok) {
        mCampaigns.value = [copyCampaign.value, ...mCampaigns.value];

        captureEvent("m_campaign_duplicated", { m_campaign_token: token });
      }

      return mCampaigns;
    } catch (err) {
      handleError(err);
    }
  }

  async function removeMCampaign(token) {
    try {
      const { response } = await useMyFetch(`/m_campaigns/${token}`).json().delete();

      if (response.value.ok) {
        mCampaigns.value = mCampaigns.value.filter((mCampaign) => mCampaign.token !== token);

        captureEvent("m_campaign_deleted", { m_campaign_token: token });
      }
      return mCampaigns;
    } catch (err) {
      handleError(err);
    }
  }

  async function syncMulticountry(token) {
    try {
      const countries = mCampaigns.value.find((campaign) => campaign.token === token)?.countries || [];
      await showModal(token, countries);
      return mCampaigns;
    } catch (err) {
      handleError(err);
    }
  }

  const mCampaignsOptions = computed(() => {
    return mCampaigns.value.map((campaign) => ({
      label: campaign.name,
      value: campaign.id,
    }));
  });

  actionDropdownMCampaigns.value = [
    {
      isDelete: false,
      function: syncMulticountry,
      text: "Sync MultiCountry",
      icon: Globe,
    },
    {
      isDelete: false,
      function: duplicateMCampaign,
      text: "duplicate",
      icon: Copy,
    },
    {
      isDelete: true,
      function: removeMCampaign,
      msgConfirm: "this campaign",
      text: "delete",
      icon: Trash,
    },
  ];

  const { resetSearchQuery } = useDebouncedSearchQuery(mCampaignsSearchQuery, () => search());

  return {
    // state
    mCampaigns,
    mCampaignsSearchQuery,
    mCampaignsCount,
    currentPage,
    surveysStatusFilter,
    mCampaignsStatusFilter,
    surveysAccountsFilter,
    actionDropdownMCampaigns,
    visibleCampaign,

    // actions
    fetchMCampaigns,
    resetAllFilters,
    changePage,
    clearFilters,
    duplicateMCampaign,
    removeMCampaign,
    syncMulticountry,
    syncMCampaignStatus,
    search,
    mCampaignsLoading,
    mCampaignsOptions,
  };
});
